/**
 * The grid's view state, carried in the page's query string.
 *
 * Creative Performance is a tool with its own URL, so the view a buyer is looking at has to be
 * linkable and has to survive a reload — "sorted by ROAS, last 30 days, as a table" is the thing
 * worth sending to a colleague, and it was unrecoverable while the grid lived in `useState` behind a
 * tab that was not in the URL either.
 *
 * Parsing is per-field and total: a hand-edited or half-written URL degrades to the defaults for the
 * fields it got wrong and keeps the ones it got right. An all-or-nothing validation would throw away
 * a good `view=table` because someone mistyped the range.
 *
 * The pure functions take a plain reader rather than a `URLSearchParams` so they can be exercised
 * without a DOM or a router.
 */
import type {
    ICreativeCustomWindow,
    ICreativeGridRange,
    ICreativeGridSortBy,
    ICreativeGridSortDir,
} from '@/api/creative-intelligence/get-creative-performance-query'
import type { ICreativeGridView } from '@/features/creative-performance/creative-performance-view-helpers'
import {
    CREATIVE_FILTER_FIELDS,
    EMPTY_CREATIVE_FILTERS,
    parseFilterValue,
    type ICreativeFilterState,
} from '@/features/creative-performance/creative-performance-filter-helpers'
import {
    DEFAULT_METRIC_KEYS,
    GRID_METRICS,
    type IGridMetricKey,
} from '@/features/creative-performance/creative-performance-metrics'
import {
    CREATIVE_PERFORMANCE_DIMENSION_FIELD_IDS,
    CREATIVE_PERFORMANCE_MAX_GROUPS,
    EMPTY_CREATIVE_PERFORMANCE_FILTER_STATE,
    assertCreativePerformanceFilterLimits,
    canonicalizeCreativePerformanceFilters,
    canonicalizeCreativePerformanceSorts,
    decodeCreativePerformanceFilters,
    encodeCreativePerformanceFilters,
    encodeCreativePerformanceSorts,
    effectiveCreativePerformanceFilterState,
    parseCreativePerformanceSorts,
    type ICreativePerformanceDimensionFieldId,
    type ICreativePerformanceFilterState,
    type ICreativePerformanceGroupOrderField,
    type ICreativePerformanceSortRule,
} from '@/features/creative-performance/creative-performance-analysis-state'
import {
    CREATIVE_TABLE_COLUMN_IDS,
    DEFAULT_TABLE_COLUMN_IDS,
    IDENTITY_COLUMN_ID,
    clampColumnWidth,
    defaultColumnWidth,
    type ICreativeTableColumnWidths,
    type ICreativeTableWidthKey,
    type ICreativeTableColumnId,
} from '@/features/creative-performance/table/creative-table-columns'

export const CREATIVE_PERFORMANCE_PARAM_KEYS = {
    Range: 'range',
    Since: 'since',
    Until: 'until',
    Sort: 'sort',
    Dir: 'dir',
    View: 'view',
    Metrics: 'metrics',
    Columns: 'columns',
    Filters: 'filters',
    Sorts: 'sorts',
    Group: 'group',
    GroupOrderBy: 'groupOrderBy',
    GroupOrderDir: 'groupOrderDir',
    /** Display flag — independent of the rich filter expression. */
    NoiseReduction: 'noiseReduction',
} as const

export interface ICreativePerformanceUrlState {
    range: ICreativeGridRange
    /**
     * The absolute window the grid is read over, present exactly while `range` is `custom`.
     *
     * Absolute rather than relative on purpose: a link or a saved view naming a flight is a
     * statement about THOSE days, and re-anchoring it to the day it is reopened would relabel a
     * different period with the same name.
     */
    customWindow: ICreativeCustomWindow | null
    sortBy: ICreativeGridSortBy
    sortDir: ICreativeGridSortDir
    view: ICreativeGridView
    /**
     * The applied threshold bounds. Their param names ARE their field names (`minSpend`, `maxRoas`, …)
     * so the browser URL, the API query and the state object all read the same — money in major
     * currency units, so a shared link stays legible and hand-editable.
     */
    filters: ICreativeFilterState
    /**
     * The rich filter EXPRESSION: grouped conditions plus the operator joining the groups. One group
     * is the simple case and serialises exactly as the flat condition list did.
     */
    filterState: ICreativePerformanceFilterState
    /** Empty means the legacy single sort aliases remain the canonical representation. */
    sorts: ICreativePerformanceSortRule[]
    /**
     * The ordered group levels, outer level first; empty means ungrouped. A ONE-level selection
     * serialises to exactly `group=<field>` — byte-identical to the single-select form it replaced —
     * so existing links and saved views round-trip without a migration or a schema-version bump.
     */
    groupBy: ICreativePerformanceDimensionFieldId[]
    groupOrderBy: ICreativePerformanceGroupOrderField
    groupOrderDir: ICreativeGridSortDir
    /**
     * Which metrics ride on each card, in pick order (the order a card stacks them). Carried in the URL
     * so the whole view is shareable and presettable — an empty selection is a valid, distinct state
     * (no metric chips), not "reset to default".
     */
    metrics: IGridMetricKey[]
    /**
     * Which columns the TABLE shows, in display order. Deliberately separate from `metrics` (which
     * drives the cards' chips): the table's job is comparison across many columns, so it must not
     * shrink to whatever four chips the grid happened to be showing. An empty selection is a valid,
     * distinct state (identity column only), not "reset to default".
     */
    columns: ICreativeTableColumnId[]
    /**
     * Per-column pixel widths, for the columns the user has actually resized. Deliberately a SPARSE
     * map rather than a width for every column: it rides in the same `columns` param as an optional
     * `id:width` suffix, so a table at default widths spends zero extra bytes and a fully-resized
     * one spends ~4 per column. That is what keeps column resize inside the 8 KB saved-view budget
     * `columns` shares with range, sort, filters and metrics.
     */
    columnWidths: ICreativeTableColumnWidths
    /**
     * Hide creatives with insufficient impressions/events. Default ON; independent of the rich
     * filter expression — a display flag persisted beside the normal filters.
     */
    noiseReduction: boolean
}

/** What the grid opens on when the URL says nothing — the values the tab shipped with. */
export const CREATIVE_PERFORMANCE_DEFAULTS: ICreativePerformanceUrlState = {
    range: '14d',
    customWindow: null,
    sortBy: 'spend',
    sortDir: 'desc',
    view: 'grid',
    // Unfiltered by default: a silent built-in threshold would hide creatives nobody asked to hide.
    filters: EMPTY_CREATIVE_FILTERS,
    filterState: EMPTY_CREATIVE_PERFORMANCE_FILTER_STATE,
    sorts: [],
    groupBy: [],
    groupOrderBy: 'label',
    groupOrderDir: 'asc',
    metrics: DEFAULT_METRIC_KEYS,
    columns: DEFAULT_TABLE_COLUMN_IDS,
    columnWidths: {},
    noiseReduction: true,
}

const RANGES: readonly ICreativeGridRange[] = ['7d', '14d', '30d', '60d', '90d', 'custom']
/** Mirrors the API's cap (`CREATIVE_PERFORMANCE_MAX_WINDOW_DAYS`); the server rejects what slips past. */
const MAX_CUSTOM_WINDOW_DAYS = 366
const MS_PER_DAY = 86_400_000

/** A `YYYY-MM-DD` param as a UTC timestamp, or `null` for anything that is not a real calendar date. */
function parseWindowDate(raw: string | null | undefined) {
    if (!raw || !/^\d{4}-\d{2}-\d{2}$/u.test(raw)) return null
    const time = Date.parse(`${raw}T00:00:00Z`)
    if (Number.isNaN(time)) return null
    // `Date.parse` accepts 2026-02-30 and rolls it into March; only a round-trip catches that.
    return new Date(time).toISOString().slice(0, 10) === raw ? time : null
}

/**
 * The absolute window a link names, or `null` when it names none it can honour — absent, malformed,
 * reversed, reaching into the future, or wider than the cap. `null` is what makes the range fall
 * back to the default while every other field of the link is kept, the per-field rule this module
 * applies everywhere.
 */
function parseCustomWindow(read: (key: string) => string | null | undefined): ICreativeCustomWindow | null {
    const since = read(CREATIVE_PERFORMANCE_PARAM_KEYS.Since)
    const until = read(CREATIVE_PERFORMANCE_PARAM_KEYS.Until)
    const sinceTime = parseWindowDate(since)
    const untilTime = parseWindowDate(until)
    if (sinceTime === null || untilTime === null || !since || !until) return null
    if (sinceTime > untilTime) return null
    if (untilTime > Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`)) return null
    if ((untilTime - sinceTime) / MS_PER_DAY + 1 > MAX_CUSTOM_WINDOW_DAYS) return null
    return { since, until }
}
const SORT_FIELDS: readonly ICreativeGridSortBy[] = ['spend', 'roas', 'cpa', 'revenue', 'impressions', 'name']
const SORT_DIRS: readonly ICreativeGridSortDir[] = ['asc', 'desc']
const VIEWS: readonly ICreativeGridView[] = ['grid', 'table']
const METRIC_KEYS: readonly IGridMetricKey[] = GRID_METRICS.map((metric) => metric.key)
const GROUP_FIELDS = CREATIVE_PERFORMANCE_DIMENSION_FIELD_IDS.filter((field) => field !== 'name')

/**
 * Parse the ordered group levels. Tolerant per level, like every other field in this parser: an
 * unknown or repeated level in a hand-edited link is dropped and the rest still apply, rather than
 * discarding the whole grouping. The API is the strict boundary — it rejects the same input.
 */
function parseGroupBy(raw: string | null | undefined): ICreativePerformanceDimensionFieldId[] {
    if (!raw) return []
    const seen = new Set<string>()
    const levels: ICreativePerformanceDimensionFieldId[] = []
    for (const token of raw.split(',')) {
        const field = token.trim()
        if (!(GROUP_FIELDS as readonly string[]).includes(field) || seen.has(field)) continue
        seen.add(field)
        levels.push(field as ICreativePerformanceDimensionFieldId)
        if (levels.length === CREATIVE_PERFORMANCE_MAX_GROUPS) break
    }
    return levels
}
const GROUP_ORDER_FIELDS: readonly ICreativePerformanceGroupOrderField[] = [
    'label',
    'creativeCount',
    'adCount',
    'spend',
    'roas',
    'purchaseValue',
    'cpa',
    'costPerResult',
    'impressions',
    'purchases',
    'results',
]

/**
 * Order-sensitive equality. Selection order is meaningful for both lists — it is the card's metric
 * stacking order, and the table's left-to-right column order — so two selections holding the same
 * keys in a different order are different views.
 */
export function sameOrderedKeys<T extends string>(a: readonly T[], b: readonly T[]): boolean {
    return a.length === b.length && a.every((key, index) => key === b[index])
}

/**
 * Read an ordered, comma-separated key list. Absent (`null`) means "the default set"; an empty
 * string means the user explicitly turned every key off. Unknown keys are dropped and duplicates
 * collapsed, so a hand-edited or older link degrades to the keys it named that still exist rather
 * than being discarded whole.
 */
function parseOrderedKeys<T extends string>(raw: string | null | undefined, allowed: readonly T[], fallback: T[]): T[] {
    if (raw === null || raw === undefined) return fallback
    const seen = new Set<T>()
    const keys: T[] = []
    for (const token of raw.split(',')) {
        const key = token.trim() as T
        if (allowed.includes(key) && !seen.has(key)) {
            seen.add(key)
            keys.push(key)
        }
    }
    return keys
}

/**
 * Read the metric selection. Absent (`null`) means "the default set"; an empty string means the user
 * explicitly turned every metric off. Unknown keys are dropped and duplicates collapsed, so a
 * hand-edited or older link degrades to the metrics it named that still exist.
 */
function parseMetrics(read: (key: string) => string | null | undefined): IGridMetricKey[] {
    return parseOrderedKeys(read(CREATIVE_PERFORMANCE_PARAM_KEYS.Metrics), METRIC_KEYS, DEFAULT_METRIC_KEYS)
}

/**
 * The table's column selection, on exactly the same degrading rules as the metric selection, plus
 * the optional `:width` suffix each token may carry.
 *
 * The suffix is stripped before the id is validated, so a resized column degrades to *the column at
 * its default width* rather than vanishing — the same per-field degradation the rest of this module
 * applies. `creative:400` is accepted as a width for the always-present identity column and never
 * enters the id list, which is why the identity column can be resized without becoming something
 * the Properties popover offers to hide.
 */
function splitColumnToken(token: string): { key: string; width: number | null } {
    const [rawKey, rawWidth] = token.trim().split(':')
    if (rawWidth === undefined) return { key: rawKey, width: null }
    const width = Number(rawWidth)
    // A non-numeric or non-finite width loses the WIDTH, not the column.
    return { key: rawKey, width: Number.isFinite(width) && width > 0 ? width : null }
}

function parseColumns(read: (key: string) => string | null | undefined): ICreativeTableColumnId[] {
    const raw = read(CREATIVE_PERFORMANCE_PARAM_KEYS.Columns)
    if (raw === null || raw === undefined) return DEFAULT_TABLE_COLUMN_IDS
    return parseOrderedKeys(
        raw
            .split(',')
            .map((token) => splitColumnToken(token).key)
            .join(','),
        CREATIVE_TABLE_COLUMN_IDS,
        DEFAULT_TABLE_COLUMN_IDS
    )
}

/** The width overrides carried alongside the column ids. Absent param means "every column default". */
function parseColumnWidths(read: (key: string) => string | null | undefined): ICreativeTableColumnWidths {
    const raw = read(CREATIVE_PERFORMANCE_PARAM_KEYS.Columns)
    if (!raw) return {}
    const widths: ICreativeTableColumnWidths = {}
    for (const token of raw.split(',')) {
        const { key, width } = splitColumnToken(token)
        if (width === null) continue
        const isResizable =
            key === IDENTITY_COLUMN_ID || CREATIVE_TABLE_COLUMN_IDS.includes(key as ICreativeTableColumnId)
        if (!isResizable) continue
        widths[key as ICreativeTableWidthKey] = clampColumnWidth(key as ICreativeTableWidthKey, width)
    }
    return widths
}

/** Narrow a raw param to one of `allowed`, or fall back — the per-field degradation rule. */
function oneOf<T extends string>(raw: string | null | undefined, allowed: readonly T[], fallback: T): T {
    return allowed.includes(raw as T) ? (raw as T) : fallback
}

/**
 * Noise reduction defaults ON. Only an explicit off (`0` / `false` / `off`) flips it; anything else
 * — absent, empty, garbage — stays on, matching the API.
 */
function parseNoiseReduction(raw: string | null | undefined): boolean {
    if (raw === null || raw === undefined || raw === '') return true
    const normalized = raw.trim().toLowerCase()
    return !(normalized === '0' || normalized === 'false' || normalized === 'off')
}

/**
 * Read the threshold bounds, dropping each unusable one on its own. `parseFilterValue` rejects empty,
 * negative and non-finite values — so a hand-mangled `?maxSpend=abc` loses that one bound rather than
 * emptying the grid or discarding the bounds that parsed.
 */
function parseFilters(read: (key: string) => string | null | undefined): ICreativeFilterState {
    const filters: ICreativeFilterState = {}
    for (const field of CREATIVE_FILTER_FIELDS) {
        const value = parseFilterValue(read(field))
        if (value !== undefined) filters[field] = value
    }
    return filters
}

export function parseCreativePerformanceParams(
    read: (key: string) => string | null | undefined
): ICreativePerformanceUrlState {
    const legacyFilters = parseFilters(read)
    let richFilters = decodeCreativePerformanceFilters(read(CREATIVE_PERFORMANCE_PARAM_KEYS.Filters))
    try {
        assertCreativePerformanceFilterLimits(effectiveCreativePerformanceFilterState(legacyFilters, richFilters))
    } catch {
        // The rich filter parameter is one independently-degrading URL field. Keep valid readable
        // legacy bounds, but discard an over-limit compact payload rather than truncating it.
        richFilters = EMPTY_CREATIVE_PERFORMANCE_FILTER_STATE
    }
    const canonicalFilters = canonicalizeCreativePerformanceFilters(legacyFilters, richFilters)
    const parsedSortBy = oneOf(
        read(CREATIVE_PERFORMANCE_PARAM_KEYS.Sort),
        SORT_FIELDS,
        CREATIVE_PERFORMANCE_DEFAULTS.sortBy
    )
    const parsedSortDir = oneOf(
        read(CREATIVE_PERFORMANCE_PARAM_KEYS.Dir),
        SORT_DIRS,
        CREATIVE_PERFORMANCE_DEFAULTS.sortDir
    )
    const canonicalSorts = canonicalizeCreativePerformanceSorts(
        parsedSortBy,
        parsedSortDir,
        parseCreativePerformanceSorts(read(CREATIVE_PERFORMANCE_PARAM_KEYS.Sorts))
    )
    const parsedRange = oneOf(read(CREATIVE_PERFORMANCE_PARAM_KEYS.Range), RANGES, CREATIVE_PERFORMANCE_DEFAULTS.range)
    const customWindow = parsedRange === 'custom' ? parseCustomWindow(read) : null
    return {
        // A `custom` range whose window did not parse degrades to the default range, not to an
        // unresolvable one — the same per-field rule the sort and the bounds follow.
        range: customWindow ? 'custom' : parsedRange === 'custom' ? CREATIVE_PERFORMANCE_DEFAULTS.range : parsedRange,
        customWindow,
        sortBy: canonicalSorts.sortBy,
        sortDir: canonicalSorts.sortDir,
        sorts: canonicalSorts.sorts,
        view: oneOf(read(CREATIVE_PERFORMANCE_PARAM_KEYS.View), VIEWS, CREATIVE_PERFORMANCE_DEFAULTS.view),
        filters: canonicalFilters.filters,
        filterState: canonicalFilters.state,
        groupBy: parseGroupBy(read(CREATIVE_PERFORMANCE_PARAM_KEYS.Group)),
        groupOrderBy: oneOf(
            read(CREATIVE_PERFORMANCE_PARAM_KEYS.GroupOrderBy),
            GROUP_ORDER_FIELDS,
            CREATIVE_PERFORMANCE_DEFAULTS.groupOrderBy
        ),
        groupOrderDir: oneOf(
            read(CREATIVE_PERFORMANCE_PARAM_KEYS.GroupOrderDir),
            SORT_DIRS,
            CREATIVE_PERFORMANCE_DEFAULTS.groupOrderDir
        ),
        metrics: parseMetrics(read),
        columns: parseColumns(read),
        columnWidths: parseColumnWidths(read),
        noiseReduction: parseNoiseReduction(read(CREATIVE_PERFORMANCE_PARAM_KEYS.NoiseReduction)),
    }
}

/**
 * An update to the view state: either a flat patch, or a function of the CURRENT state.
 *
 * The functional form exists because writes can outrun reads. A write goes out through
 * `router.replace`, and the state a component holds only refreshes once that round-trips back through
 * `useSearchParams`. Two updates fired inside that window (the debounced min and max of one metric,
 * two quick preset clicks) both computed from the same captured snapshot, so the second overwrote the
 * first and silently dropped a bound — which breaks the whole point of combining bounds. An updater
 * is evaluated against the freshest state at fire time instead.
 */
export type ICreativePerformanceStateUpdate =
    | Partial<ICreativePerformanceUrlState>
    | ((current: ICreativePerformanceUrlState) => Partial<ICreativePerformanceUrlState>)

/**
 * Apply an update to a base state. Pure, and the single place the patch-or-updater distinction is
 * resolved, so the hook and its tests agree by construction.
 */
export function mergeCreativePerformanceState(
    base: ICreativePerformanceUrlState,
    update: ICreativePerformanceStateUpdate
): ICreativePerformanceUrlState {
    const merged = { ...base, ...(typeof update === 'function' ? update(base) : update) }
    // The range and its window are ONE fact in two fields. A patch that moves the range to a preset
    // drops a now-meaningless window, and a `custom` range without one names a window the grid
    // cannot read — so neither half is ever left standing without the other.
    const customWindow = merged.range === 'custom' ? merged.customWindow : null
    const range = merged.range === 'custom' && !customWindow ? CREATIVE_PERFORMANCE_DEFAULTS.range : merged.range
    assertCreativePerformanceFilterLimits(effectiveCreativePerformanceFilterState(merged.filters, merged.filterState))
    const filters = canonicalizeCreativePerformanceFilters(merged.filters, merged.filterState)
    const sorts = canonicalizeCreativePerformanceSorts(merged.sortBy, merged.sortDir, merged.sorts)
    return {
        ...merged,
        range,
        customWindow,
        filters: filters.filters,
        filterState: filters.state,
        sortBy: sorts.sortBy,
        sortDir: sorts.sortDir,
        sorts: sorts.sorts,
    }
}

/**
 * Serialise the state, omitting every field sitting at its default. A first visit that touches
 * nothing should leave the address bar as it found it rather than accreting four redundant params.
 */
export function toCreativePerformanceParams(state: ICreativePerformanceUrlState): URLSearchParams {
    const params = new URLSearchParams()
    const {
        Range,
        Since,
        Until,
        Sort,
        Dir,
        View,
        Metrics,
        Columns,
        Filters,
        Sorts,
        Group,
        GroupOrderBy,
        GroupOrderDir,
        NoiseReduction,
    } = CREATIVE_PERFORMANCE_PARAM_KEYS
    assertCreativePerformanceFilterLimits(effectiveCreativePerformanceFilterState(state.filters, state.filterState))
    const canonicalFilters = canonicalizeCreativePerformanceFilters(state.filters, state.filterState)
    const canonicalSorts = canonicalizeCreativePerformanceSorts(state.sortBy, state.sortDir, state.sorts)
    // A custom window writes all three params together; a `custom` range without one is not written
    // at all, so a malformed state serialises to the default range rather than to an unreadable link.
    if (state.range === 'custom') {
        if (state.customWindow) {
            params.set(Range, state.range)
            params.set(Since, state.customWindow.since)
            params.set(Until, state.customWindow.until)
        }
    } else if (state.range !== CREATIVE_PERFORMANCE_DEFAULTS.range) {
        params.set(Range, state.range)
    }
    if (canonicalSorts.sortBy !== CREATIVE_PERFORMANCE_DEFAULTS.sortBy) params.set(Sort, canonicalSorts.sortBy)
    if (canonicalSorts.sortDir !== CREATIVE_PERFORMANCE_DEFAULTS.sortDir) params.set(Dir, canonicalSorts.sortDir)
    if (canonicalSorts.sorts.length) params.set(Sorts, encodeCreativePerformanceSorts(canonicalSorts.sorts))
    if (state.view !== CREATIVE_PERFORMANCE_DEFAULTS.view) params.set(View, state.view)
    // Only bounds that are set — an unfiltered grid leaves the address bar as it found it.
    for (const field of CREATIVE_FILTER_FIELDS) {
        const value = canonicalFilters.filters[field]
        if (value !== undefined) params.set(field, String(value))
    }
    const encodedFilters = encodeCreativePerformanceFilters(canonicalFilters.state)
    if (encodedFilters) params.set(Filters, encodedFilters)
    if (state.groupBy.length) params.set(Group, state.groupBy.join(','))
    if (state.groupBy.length && state.groupOrderBy !== CREATIVE_PERFORMANCE_DEFAULTS.groupOrderBy) {
        params.set(GroupOrderBy, state.groupOrderBy)
    }
    if (state.groupBy.length && state.groupOrderDir !== CREATIVE_PERFORMANCE_DEFAULTS.groupOrderDir) {
        params.set(GroupOrderDir, state.groupOrderDir)
    }
    // Omit the metrics param at the default selection (a first visit keeps a clean URL); an explicit
    // empty selection serialises to `metrics=` so it round-trips as "no metrics", not "reset".
    if (!sameOrderedKeys(state.metrics, DEFAULT_METRIC_KEYS)) params.set(Metrics, state.metrics.join(','))
    // Same rule for the table's columns: omitted at the default configuration so a first visit
    // leaves the address bar clean, and serialised as a compact CSV rather than an object because
    // a saved view caps its whole blob at 8 KB and compares views on the serialised string.
    // The identity column is not in `state.columns` (it is always present and always first), so its
    // width rides as a leading `creative:N` token — emitted ONLY when resized.
    const identityWidth = state.columnWidths[IDENTITY_COLUMN_ID]
    const identityToken =
        identityWidth !== undefined && identityWidth !== defaultColumnWidth(IDENTITY_COLUMN_ID)
            ? [`${IDENTITY_COLUMN_ID}:${clampColumnWidth(IDENTITY_COLUMN_ID, identityWidth)}`]
            : []

    // A column at its default width writes its bare id, so the param only grows for columns the user
    // actually dragged — the property that keeps this inside the saved-view budget.
    const columnTokens = state.columns.map((id) => {
        const width = state.columnWidths[id]
        if (width === undefined || width === defaultColumnWidth(id)) return id
        return `${id}:${clampColumnWidth(id, width)}`
    })

    const tokens = [...identityToken, ...columnTokens]
    const isDefault =
        identityToken.length === 0 &&
        sameOrderedKeys(state.columns, DEFAULT_TABLE_COLUMN_IDS) &&
        columnTokens.every((token, index) => token === state.columns[index])
    if (!isDefault) params.set(Columns, tokens.join(','))
    // Default ON is omitted; only an explicit off lands in the URL.
    if (!state.noiseReduction) params.set(NoiseReduction, '0')
    return params
}
